import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useAuth } from './AuthContext';
import {config} from "../config/appConfig";

// --- Configuration ---
const SOCKET_URL = config.websocket.url; // Your Spring Boot WebSocket endpoint
const DEBUG_STOMP = false; // Set to true for very verbose STOMP frame logging

const WebSocketContext = createContext({
  stompClient: null,
  isConnected: false,
  sendMessage: (destination, body) => { console.warn("WS not connected - sendMessage called"); },
  subscribe: (topic, callback) => { console.warn("WS not connected - subscribe called"); return () => {}; },
});

export const WebSocketProvider = ({ children }) => {
  const { isAuthenticated, token, isLoading: isAuthLoading } = useAuth(); // Use Auth context
  const [stompClient, setStompClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscriptionsRef = useRef({}); // Store active subscriptions { topic: subscriptionObject }
  const clientRef = useRef(null);

  // --- Connection Logic ---
  const connect = useCallback(() => {
    if (!isAuthenticated || !token || clientRef.current?.active) {
        console.log(`WS Connect Guard: Auth=${isAuthenticated}, Token=${!!token}, Active=${!!clientRef.current?.active}`);
        if (clientRef.current?.active) console.log("-> WS connection attempt ignored: Already active.");
        if (!isAuthenticated || !token) console.log("-> WS connection attempt ignored: Not authenticated.");
        return; // Don't connect if not authenticated or already connected/connecting
    }
    console.log(`%cAttempting WebSocket connection with token... addr:${SOCKET_URL}`, 'color: blue; font-weight: bold;');

    const client = new Client({
        webSocketFactory: () => new SockJS(SOCKET_URL),
        connectHeaders: { // <-- Send JWT token here
            Authorization: `Bearer ${token}`,
            // Add other headers if needed
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        debug: (str) => { if (DEBUG_STOMP) console.debug('STOMP DEBUG: ', str); },
        onConnect: (frame) => {
            console.log('%cSTOMP Connected:', 'color: green; font-weight: bold;', frame);
            setIsConnected(true);
            // Re-subscribe logic (if needed, maybe handled by components themselves)
        },
        onDisconnect: () => {
            console.warn('STOMP Disconnected');
            setIsConnected(false);
            subscriptionsRef.current = {}; // Clear subscriptions on disconnect
            clientRef.current = null; // Clear client ref on disconnect
            setStompClient(null); // Update state
        },
        onStompError: (frame) => {
            console.error('%cSTOMP Broker Error:', 'color: red; font-weight: bold;', frame.headers['message'], frame);
            setIsConnected(false);
        },
        onWebSocketError: (event) => {
            console.error('%cWebSocket Transport Error:', 'color: red; font-weight: bold;', event);
            setIsConnected(false);
        },
        onWebSocketClose: (event) => {
            console.warn(`WebSocket Closed: Code ${event.code}`);
            setIsConnected(false);
            // Attempt reconnect on unclean close? STOMP client handles this based on reconnectDelay
        }
    });

    clientRef.current = client; // Store client instance in ref
    setStompClient(client); // Update state (optional, ref might be enough)

    try {
        console.log("Activating STOMP client...");
        client.activate();
    } catch (error) {
        console.error("Error activating STOMP client:", error);
        setIsConnected(false);
        clientRef.current = null;
        setStompClient(null);
    }
}, [isAuthenticated, token]); // Depend on auth state and token

// --- Disconnection Logic ---
 const disconnectWebSocket = useCallback(() => {
     console.log("Attempting explicit WebSocket disconnect...");
     if (clientRef.current) { // Use ref here
          try {
              clientRef.current.deactivate();
              console.log("STOMP client deactivated.");
          } catch (error) {
              console.error("Error during STOMP client deactivation:", error);
          } finally {
              setIsConnected(false);
              subscriptionsRef.current = {};
              clientRef.current = null; // Clear the ref
              setStompClient(null); // Clear state
               console.log("WebSocket state explicitly reset.");
          }
      } else {
          console.log("Explicit disconnect ignored: No active client ref.");
      }
 }, []); // No dependencies needed for explicit disconnect


 // --- Connect/Disconnect based on Auth State ---
 useEffect(() => {
     // Don't try to connect while auth is still loading
     if (isAuthLoading) {
          console.log("WS Effect: Auth is loading, skipping connection check.");
          return;
     }

     if (isAuthenticated && token) {
         console.log("WS Effect: Authenticated, attempting connection.");
         connect();
     } else {
         console.log("WS Effect: Not authenticated, ensuring disconnection.");
         disconnectWebSocket();
     }

     // Cleanup on unmount OR when auth state changes to false
     return () => {
         console.log("WS Effect cleanup: Ensuring disconnection.");
         disconnectWebSocket();
     };
 }, [isAuthenticated, token, connect, disconnectWebSocket, isAuthLoading]); // Add isAuthLoading


// --- Send Message ---
 const sendMessage = useCallback((logicalDestination, body = {}) => { // Parameter name implies it expects the logical part
      // Prepend the application prefix automatically HERE
      const fullDestination = '/app' + logicalDestination; // <<< KEEP THIS LINE
      const messageBody = JSON.stringify(body);

      if (clientRef.current && clientRef.current.active && isConnected) {
        try {
          // Publish to the *full* destination
          clientRef.current.publish({ destination: fullDestination, body: messageBody });
          console.log(`%cWS Sent OK to ${fullDestination}`, 'color: purple;'); // Log the full path
        } catch (error) {
           console.error(`%cError sending WS message to ${fullDestination}:`, 'color: red;', error);
        }
      } else {
        console.warn(`%cWS Send FAILED: Not connected/active. Dest: ${fullDestination}`, 'color: orange;');
      }
    }, [isConnected]); // isConnected ensures we don't send before STOMP onConnect

// --- Subscribe to Topic ---
 const subscribe = useCallback((topic, callback) => {
     console.log(`Attempting to subscribe to topic: ${topic}`);
     // Use clientRef.current for subscribing
     if (clientRef.current && clientRef.current.active && isConnected) {
         if (subscriptionsRef.current[topic]) {
             console.warn(`Re-subscribing to ${topic}. Unsubscribing previous.`);
             try { subscriptionsRef.current[topic].unsubscribe(); } catch (e) { console.error("Error unsubscribing:", e); }
             delete subscriptionsRef.current[topic];
         }

         console.log(`%cSubscribing to ${topic}...`, 'color: cyan;');
         try {
             const subscription = clientRef.current.subscribe(topic, (message) => {
                 console.log(`%c>>> MSG on ${topic}`, 'color: lime;', message.body);
                 try {
                     const parsedBody = JSON.parse(message.body);
                     callback(parsedBody, null);
                 } catch (error) {
                     console.error(`%cError parsing message from ${topic}:`, 'color: red;', error, 'Raw:', message.body);
                     callback(null, error);
                 }
             });
             subscriptionsRef.current[topic] = subscription;
             console.log(`%cSubscribed OK to ${topic}.`, 'color: cyan;');
             // Return unsubscribe function
             return () => {
                  if (subscriptionsRef.current[topic]) {
                      console.log(`%cUnsubscribing from ${topic}...`, 'color: magenta;');
                      try { subscriptionsRef.current[topic].unsubscribe(); } catch (e) { console.error("Error unsubscribing:", e); }
                      delete subscriptionsRef.current[topic];
                  }
             };
         } catch (error) {
             console.error(`%cFailed to subscribe to ${topic}:`, 'color: red;', error);
             return () => {};
         }
     } else {
         console.warn(`%cWS Subscribe FAILED: Not connected/active. Topic: ${topic}`, 'color: orange;');
         return () => {};
     }
 }, [isConnected]); // Depend on isConnected

return (
  <WebSocketContext.Provider value={{ stompClient, isConnected, sendMessage, subscribe }}>
    {children}
  </WebSocketContext.Provider>
);
};

export default WebSocketContext;